import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, BarChart3, ShieldCheck, Link2Off } from "lucide-react";
import { fetchSharedReport } from "@/lib/api";
import { WeeklyChart, BestPin, PinTable, EN_L } from "@/components/vendor/AdInsights";

const T = {
  en: {
    locale: "en-IN", badge: "Read-only shared report · valid till", loading: "Loading report…", notAvail: "Report not available",
    goHome: "Go to VerB", impressions: "impressions", clicks: "clicks", ctr: "CTR", noData: "No data available",
    note: "Read-only summary of actually recorded impressions and clicks. Contains no customer information.",
    ctrHelp: "Click rate = clicks ÷ impressions × 100 (N/A when there are no impressions).",
    status: { active: "Active", paused: "Paused", expired: "Expired", pending_approval: "Pending approval", pending_payment: "Pending payment", rejected: "Rejected" },
    type: { product: "Product", shop: "Shop", service: "Service" },
    L: EN_L,
  },
  hi: {
    locale: "hi-IN", badge: "केवल देखने योग्य साझा रिपोर्ट · मान्य तिथि तक", loading: "रिपोर्ट लोड हो रही है…", notAvail: "रिपोर्ट उपलब्ध नहीं है",
    goHome: "VerB पर जाएँ", impressions: "इम्प्रेशन", clicks: "क्लिक", ctr: "क्लिक दर", noData: "कोई डेटा उपलब्ध नहीं",
    note: "वास्तव में दर्ज किए गए इम्प्रेशन और क्लिक का केवल-देखने योग्य सारांश। इसमें ग्राहक की कोई जानकारी नहीं है।",
    ctrHelp: "क्लिक दर = क्लिक ÷ इम्प्रेशन × 100 (इम्प्रेशन न होने पर लागू नहीं)।",
    status: { active: "सक्रिय", paused: "रुका हुआ", expired: "समाप्त", pending_approval: "स्वीकृति लंबित", pending_payment: "भुगतान लंबित", rejected: "अस्वीकृत" },
    type: { product: "उत्पाद", shop: "दुकान", service: "सेवा" },
    L: { weekly: "साप्ताहिक प्रदर्शन (सप्ताह की शुरुआत)", allPins: "सभी PIN", noData: "कोई डेटा उपलब्ध नहीं", impressions: "इम्प्रेशन", clicks: "क्लिक", topPin: "सबसे ज़्यादा क्लिक वाला PIN:", noClicks: "इस अवधि में अभी तक कोई क्लिक दर्ज नहीं हुआ", pin: "PIN", area: "क्षेत्र", ctr: "क्लिक दर", unverified: "असत्यापित", na: "लागू नहीं" },
  },
};

function ErrorView({ msg, t }) {
  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="shared-report-error">
      <Link2Off className="w-10 h-10 text-slate-300 mx-auto mb-2" />
      <div className="font-display text-lg font-bold">{t.notAvail}</div>
      <p className="text-sm text-slate-500 mt-1" data-testid="shared-report-error-msg">{msg}</p>
      <Link to="/" className="inline-block mt-4 text-sm font-semibold text-emerald-700 hover:underline">{t.goHome}</Link>
    </div>
  );
}

export default function SharedAdReport() {
  const { token } = useParams();
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex";
    document.head.appendChild(m);
    fetchSharedReport(token).then(setRep).catch((e) => {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "object" && d ? d : { message: d || T.en.notAvail, lang: "en" });
    });
    return () => m.remove();
  }, [token]);

  if (err) return <ErrorView msg={err.message} t={T[err.lang] || T.en} />;
  if (!rep) return <div className="py-16 text-center text-slate-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline" /> {T.en.loading}</div>;
  const t = T[rep.lang] || T.en;
  const fmtD = (d) => (d ? new Date(d).toLocaleDateString(t.locale) : null);
  const ctr = rep.totals.ctr === null || rep.totals.ctr === undefined ? t.L.na : `${Number(rep.totals.ctr).toFixed(2)}%`;
  const c = rep.campaign;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10" lang={rep.lang} data-testid="shared-report-page">
      <div className="text-[11px] inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-800 px-2.5 py-1 mb-3" data-testid="shared-report-badge"><ShieldCheck className="w-3.5 h-3.5" /> {t.badge} {fmtD(rep.expires_at)}</div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold" data-testid="shared-report-title">{c.name}</h1>
      <div className="text-sm text-slate-500 mt-1" data-testid="shared-report-meta">{c.target_name && c.target_name !== c.name ? `${c.target_name} · ` : ""}{t.type[c.target_type] || c.target_type} · {t.status[c.status] || c.status}{rep.period_from ? ` · ${fmtD(rep.period_from)} – ${fmtD(rep.period_to)}` : ""}</div>
      <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50/60 p-3 sm:p-4">
        <div className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1 mb-2" data-testid="shared-report-totals"><BarChart3 className="w-3.5 h-3.5 text-emerald-700" /> {rep.has_data ? `${rep.totals.impressions} ${t.impressions} · ${rep.totals.clicks} ${t.clicks} · ${ctr} ${t.ctr}` : t.noData}</div>
        {rep.has_data ? (
          <>
            <BestPin adId="shared" rep={rep} L={t.L} />
            <WeeklyChart adId="shared" weekly={rep.weekly} pins={rep.pins} L={t.L} locale={t.locale} />
            <PinTable rep={rep} compact L={t.L} />
          </>
        ) : <div className="rounded-lg bg-white border border-dashed border-slate-200 text-center text-xs text-slate-500 py-5" data-testid="shared-report-no-data">{t.noData}</div>}
        <div className="text-[10px] text-slate-400 mt-2">{t.note} {t.ctrHelp}</div>
      </div>
    </div>
  );
}
